import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  AfterViewChecked,
  HostListener,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { CdkDrag } from '@angular/cdk/drag-drop';
import { ChatbotService } from './chatbot.service';
import { SharedService } from '../shared/services/shared.service';

import { ChangeDetectorRef } from '@angular/core';

@Component({
    selector: 'app-chatbot',
    templateUrl: './chatbot.component.html',
    styleUrl: './chatbot.component.scss',
    standalone: true,
    imports: [FormsModule, NgClass, CdkDrag]
})
export class ChatbotComponent implements OnInit, AfterViewChecked {
  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;
  private shouldScrollToBottom = false;

  // Custom resizing state
  chatWidth = 350;
  chatHeight = 500;
  private isResizing = false;
  private startX = 0;
  private startY = 0;
  private startWidth = 0;
  private startHeight = 0;

  constructor(
    public chatbotService: ChatbotService,
    public sharedService: SharedService,
    private cdr: ChangeDetectorRef
  ) {
    this.initSpeechRecognition();
  }

  // --- Voice Integration ---
  recognition: any;
  isRecording = false;

  private initSpeechRecognition() {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SpeechRecognition) {
      this.recognition = new SpeechRecognition();
      this.recognition.continuous = false;
      this.recognition.interimResults = true;

      this.recognition.onresult = (event: any) => {
        let finalTranscript = '';
        let interimTranscript = '';
        for (let i = event.resultIndex; i < event.results.length; ++i) {
          if (event.results[i].isFinal) {
            finalTranscript += event.results[i][0].transcript;
          } else {
            interimTranscript += event.results[i][0].transcript;
          }
        }
        
        // Update input field in real-time
        if (finalTranscript) {
           this.chatbotService.userInput = finalTranscript;
           this.sendMessage();
        } else {
           this.chatbotService.userInput = interimTranscript;
        }
        this.cdr.detectChanges();
      };

      this.recognition.onend = () => {
        this.isRecording = false;
        this.cdr.detectChanges();
      };
      
      this.recognition.onerror = (event: any) => {
        console.error('Speech recognition error', event.error);
        this.isRecording = false;
        this.cdr.detectChanges();
      };
    } else {
      console.warn("Speech Recognition API not supported in this browser.");
    }
  }

  toggleRecording() {
    if (!this.chatbotService.isVoiceInputEnabled || !this.recognition) return;
    
    if (this.isRecording) {
      this.recognition.stop();
    } else {
      this.recognition.start();
      this.isRecording = true;
    }
  }

  toggleVoiceInput() {
    this.chatbotService.isVoiceInputEnabled = !this.chatbotService.isVoiceInputEnabled;
    if (!this.chatbotService.isVoiceInputEnabled && this.isRecording) {
      this.recognition?.stop();
    }
  }

  toggleVoiceOutput() {
    this.chatbotService.isVoiceOutputEnabled = !this.chatbotService.isVoiceOutputEnabled;
    if (!this.chatbotService.isVoiceOutputEnabled) {
      this.chatbotService.stopTTS();
    }
  }

  ngOnInit(): void {
    // Add a personalized welcome message only if the chat history is empty
    if (this.chatbotService.messages.length === 0) {
      this.sharedService.company_info_retrieved$.subscribe(() => {
        if (this.chatbotService.messages.length === 0) {
          const userName = this.sharedService.logged_in_user?.name_first 
            ? ` ${this.sharedService.logged_in_user.name_first}` 
            : '';
            
          this.chatbotService.messages.push({
            text: `Hello${userName}! How can I help you?`,
            isUser: false,
          });
          this.cdr.detectChanges();
        }
      });
    }
    this.shouldScrollToBottom = true;
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  private scrollToBottom(): void {
    try {
      if (this.messagesContainer) {
        this.messagesContainer.nativeElement.scrollTop =
          this.messagesContainer.nativeElement.scrollHeight;
      }
    } catch (err) {
      console.error('Error scrolling to bottom:', err);
    }
  }

  openChatbot() {
    this.chatbotService.isOpen = true;
    // Scroll to bottom when opening
    setTimeout(() => {
      this.shouldScrollToBottom = true;
    }, 100);
  }

  closeChatbot() {
    this.chatbotService.isOpen = false;
  }

  sendMessage() {
  if (this.chatbotService.userInput.trim()) {
    const userMessage = this.chatbotService.userInput;

    this.chatbotService.messages.push({
      text: userMessage,
      isUser: true,
    });
    this.shouldScrollToBottom = true;

    this.chatbotService.isTyping = true;
    this.cdr.detectChanges(); // Force DOM update so the typing indicator renders before scrolling
    
    this.chatbotService.sendMessage(userMessage).subscribe({
      next: (response: any) => {
        console.warn('Bot response:', response);
        
        // Use the HTML response directly
        const responseText = response.source === 'local' 
          ? `${response.botResponse} <br><small><em>(from FAQ)</em></small>` 
          : response.botResponse;
          
        this.chatbotService.messages.push({
          text: responseText,
          isUser: false,
        });

        this.chatbotService.isTyping = false;
        this.cdr.detectChanges();
        
        if (this.chatbotService.isVoiceOutputEnabled) {
            const cleanText = this.stripHtmlTags(response.botResponse);
            this.chatbotService.playTTS(cleanText);
        }
        
        this.shouldScrollToBottom = true;
      },
      error: (error) => {
        console.error('Chatbot error:', error);
        this.chatbotService.messages.push({
          text: 'Sorry, something went wrong. Please try again.',
          isUser: false,
        });

        this.chatbotService.isTyping = false;
        this.cdr.detectChanges();
        this.shouldScrollToBottom = true;
      }
    });

    this.chatbotService.userInput = '';
  }
}

  clearConversation() {
    this.chatbotService.messages = [];
    this.chatbotService.resetSelectionState();
    console.log('Conversation cleared');
  }

  stripHtmlTags(text: string): string {
    if (!text) return '';
    let cleanText = text.replace(/<[^>]*>?/gm, '');
    // Removed whitespace squashing to preserve line breaks and formatting
    return cleanText.trim(); 
  }

  // --- Custom Resize Logic ---

  onResizeStart(event: MouseEvent) {
    this.isResizing = true;
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.startWidth = this.chatWidth;
    this.startHeight = this.chatHeight;
    event.preventDefault(); // prevent text selection
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent) {
    if (!this.isResizing) return;
    
    // Mouse moving left (smaller X) = INCREASING width (because right edge is fixed)
    const diffX = this.startX - event.clientX;
    
    // Mouse moving up (smaller Y) = INCREASING height (because bottom edge is fixed)
    const diffY = this.startY - event.clientY;

    // Constrain width and height
    this.chatWidth = Math.max(300, Math.min(this.startWidth + diffX, window.innerWidth * 0.9));
    this.chatHeight = Math.max(400, Math.min(this.startHeight + diffY, window.innerHeight * 0.8));
  }

  @HostListener('document:mouseup')
  onMouseUp() {
    this.isResizing = false;
  }
}
